import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Renders a guide article body with the LiberaCure editorial design tokens.
//
// Typography pairs the same as Honestly/Mission/PolicyPage:
//   - H1/H2: Cormorant Garamond (font-display), forest-900, italic on H1
//   - Body: Outfit sans, warm-700, generous line-height for long-form
//   - Accent: gold-700 for links + bullet markers
//   - Tables: cream-50 header on warm-200 borders, sized for SNS-length data anchors
//
// Strong/em get their own subtle weight changes — strong is forest-900 not
// blanket-bold, em stays warm-700 italic. Reads quieter than a typical blog
// post, which is the whole point of the voice baseline.

interface Props {
  content: string;
  className?: string;
}

const GuideMarkdown: React.FC<Props> = ({ content, className = '' }) => {
  return (
    <div className={`guide-prose ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="font-display italic text-display-md sm:text-display-lg text-forest-900 leading-[1.05] tracking-tight mb-10 mt-0">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="font-display text-[26px] sm:text-[32px] text-forest-900 leading-[1.2] tracking-tight mt-16 mb-6 pt-2 border-t border-warm-200">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="font-display text-[20px] sm:text-[22px] text-forest-900 leading-snug tracking-tight mt-10 mb-4">
              {children}
            </h3>
          ),
          p: ({ children }) => (
            <p className="text-[17px] sm:text-[18px] text-warm-700 leading-[1.75] mb-5">
              {children}
            </p>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="text-gold-700 underline decoration-gold-300 underline-offset-2 hover:text-gold-800 hover:decoration-gold-600 transition-colors"
            >
              {children}
            </a>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-forest-900">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic text-warm-700">{children}</em>
          ),
          ul: ({ children }) => (
            <ul className="space-y-2.5 mb-6 pl-0">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="space-y-2.5 mb-6 pl-6 list-decimal marker:text-gold-600 marker:font-accent">
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => {
            // Distinguish ul vs ol items: ol gets default list-style, ul gets gold dot.
            const parent = (props as any).node?.parent?.type;
            if (parent === 'orderedList') {
              return <li className="text-[17px] text-warm-700 leading-[1.7] pl-2">{children}</li>;
            }
            return (
              <li className="flex items-start gap-3 text-[17px] text-warm-700 leading-[1.7]">
                <span className="mt-[0.7em] w-[5px] h-[5px] rounded-full bg-gold-500 flex-shrink-0" />
                <span className="flex-1">{children}</span>
              </li>
            );
          },
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-gold-400 pl-5 py-1 my-7 text-[16px] text-warm-600 italic font-accent leading-[1.7]">
              {children}
            </blockquote>
          ),
          hr: () => (
            <div className="flex items-center justify-center my-12" aria-hidden="true">
              <div className="h-px bg-warm-200 flex-1 max-w-[110px]" />
              <div className="mx-5 flex items-center gap-2">
                <div className="w-1 h-1 rounded-full bg-gold-400/70" />
                <div className="w-1.5 h-1.5 rounded-full bg-gold-500" />
                <div className="w-1 h-1 rounded-full bg-gold-400/70" />
              </div>
              <div className="h-px bg-warm-200 flex-1 max-w-[110px]" />
            </div>
          ),
          table: ({ children }) => (
            <div className="my-7 overflow-x-auto rounded-lg border border-warm-200">
              <table className="w-full text-[14px] sm:text-[15px] border-collapse">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-cream-100 border-b border-warm-200">{children}</thead>
          ),
          th: ({ children }) => (
            <th className="text-left px-4 py-3 font-display text-forest-900 font-semibold">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-3 text-warm-700 border-t border-warm-100 align-top">
              {children}
            </td>
          ),
          code: ({ children, className: cls }) => {
            const isBlock = cls?.includes('language-');
            if (isBlock) {
              return (
                <pre className="bg-forest-900 text-cream-100 rounded-lg p-5 text-[13px] leading-relaxed overflow-x-auto my-6">
                  <code>{children}</code>
                </pre>
              );
            }
            return (
              <code className="bg-cream-100 text-forest-800 px-1.5 py-0.5 rounded text-[0.9em] font-mono">
                {children}
              </code>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default GuideMarkdown;
